import { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  Alert,
} from "react-native";
import { useRoute, RouteProp } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { Feather, Ionicons } from "@expo/vector-icons";
import { ProductGrid } from "../components/products/ProductGrid";
import { useNavigation } from "@react-navigation/native";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { Button } from "../components/ui/Button";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../types/navigation";
import type { Product } from "../types/supabase";
import { useAuth } from "../hooks/useAuth";
import { findOrCreateChat } from "../utils/chatUtils";
import { VendorRatingsModal } from "../components/vendors/VendorRatingsModal";
import { StoreHoursDisplay } from "../components/vendors/StoreHoursDisplay";
import { TouchableOpacity } from "react-native";
import { useVendorRatings } from "../hooks/useVendorRatings";
import { EmptyState } from "../components/ui/EmptyState";

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type VendorScreenRouteProp = RouteProp<RootStackParamList, "Vendor">;

interface Vendor {
  id: string;
  store_name: string;
  description?: string;
  borough?: string;
  business_type?: string;
  banner_image?: string;
  logo_image?: string;
  user_id: string;
  city?: string;
  state?: string;
  country?: string;
  delivery_hours?: {
    [key: string]: {
      isOpen: boolean;
      openTime: string;
      closeTime: string;
    };
  };
}

export default function VendorScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const route = useRoute<VendorScreenRouteProp>();
  const navigation = useNavigation<NavigationProp>();
  const vendorId = route.params.id;
  const { user } = useAuth();
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [showRatingsModal, setShowRatingsModal] = useState(false);
  
  // Fetch vendor ratings
  const { data: ratingsData } = useVendorRatings(vendorId);

  const { data: vendor, isLoading: isLoadingVendor } = useQuery({
    queryKey: ["vendor", vendorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendors")
        .select("*")
        .eq("id", vendorId)
        .single();

      if (error) throw error;
      return data as Vendor;
    },
    enabled: !!vendorId,
  });

  // Add query to fetch the vendor owner's profile with username
  const { data: vendorOwnerProfile } = useQuery({
    queryKey: ["vendor-owner-profile", vendor?.user_id],
    queryFn: async () => {
      if (!vendor?.user_id) return null;

      console.log("Fetching vendor owner profile for user ID:", vendor.user_id);
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, full_name")
        .eq("id", vendor.user_id)
        .single();

      if (error) {
        console.error("Error fetching vendor owner profile:", error);
        return null;
      }

      console.log("Vendor owner profile found:", data);
      return data;
    },
    enabled: !!vendor?.user_id,
  });

  const { data: products, isLoading: isLoadingProducts } = useQuery({
    queryKey: ["vendor-products", vendorId],
    queryFn: async () => {
      console.log("[PRODUCTS] Query started with vendorId:", vendorId);
      try {
        console.log("[PRODUCTS] Query started with vendorId:", vendorId);
        const { data, error } = await supabase
          .from("products")
          .select("*")
          .eq("vendor_id", vendorId)
          .eq("status", "active")
          .gt("quantity", 0);
        if (error) throw error;
        console.log("[PRODUCTS] Fetched vendor products:", data);
        return data as Product[];
      } catch (error) {
        console.error("[PRODUCTS] Query failed:", error);
        throw error;
      }
    },
    enabled: !!vendorId,
    staleTime: 0,
  });

  const startChat = async () => {
    // Validate prerequisites
    if (!vendor || !vendor.id || !vendor.user_id) {
      Alert.alert("Error", "Unable to start chat - vendor information is missing.");
      return;
    }

    if (!user) {
      Alert.alert("Error", "You must be signed in to message sellers.");
      return;
    }

    if (vendor.user_id === user.id) {
      Alert.alert("Error", "You cannot message yourself.");
      return;
    }

    setIsStartingChat(true);

    try {
      console.log(`[CHAT] Starting chat between user ${user.id} and vendor user ${vendor.user_id} (vendor ${vendor.id})`);

      // Find or create chat using user IDs (not vendor_id) to ensure consistent chat grouping
      const chatId = await findOrCreateChat(user.id, vendor.user_id, vendor.id);
      
      console.log(`[CHAT] Navigating to chat: ${chatId}`);
      navigation.navigate("Chat", { id: chatId });
      
    } catch (error) {
      console.error("[CHAT] Error starting chat:", error);
      Alert.alert(
        "Chat Error",
        error instanceof Error ? error.message : "Unable to start chat. Please try again.",
        [{ text: "OK" }]
      );
    } finally {
      setIsStartingChat(false);
    }
  };

  if (isLoadingVendor || isLoadingProducts) {
    return (
      <View style={styles.loading}>
        <Text>Loading...</Text>
      </View>
    );
  }

  if (!vendor) {
    return (
      <View style={styles.loading}>
        <Text>Vendor not found</Text>
      </View>
    );
  }

  const renderHeader = () => {
    console.log("Rendering vendor profile with owner:", vendorOwnerProfile);
    return (
      <>
        {/* Banner Image */}
        {vendor.banner_image ? (
          <Image
            source={{ uri: vendor.banner_image }}
            style={styles.banner}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.bannerPlaceholder} />
        )}

        {/* Store Info */}
        <View style={styles.storeInfo}>
          <View style={styles.logoContainer}>
            {vendor.logo_image ? (
              <Image source={{ uri: vendor.logo_image }} style={styles.logo} />
            ) : (
              <View style={styles.logoPlaceholder}>
                <Feather name="shopping-bag" size={32} color="#666" />
              </View>
            )}
            <TouchableOpacity 
              style={styles.ratingBadge}
              onPress={() => setShowRatingsModal(true)}
            >
              <Ionicons name="star-outline" size={14} color={colors.primary} />
              <Text style={styles.ratingText}>
                {ratingsData?.averageRating ? ratingsData.averageRating.toFixed(1) : '—'}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.storeDetails}>
            <Text style={styles.storeName}>{vendor.store_name}</Text>
            {vendorOwnerProfile?.username && (
              <View style={styles.usernameContainer}>
                <Text style={styles.username}>
                  @{vendorOwnerProfile.username}
                </Text>
              </View>
            )}
            <Text style={styles.storeDescription}>{vendor.description}</Text>

            <View style={styles.storeMetadata}>
              <View style={styles.metadataItem}>
                <Feather name="map-pin" size={16} color="#666" />
                <Text style={styles.metadataText}>{vendor.city}</Text>
              </View>
              {vendor.business_type && (
                <View style={styles.metadataItem}>
                  <Feather name="tag" size={16} color="#666" />
                  <Text style={styles.metadataText}>
                    {vendor.business_type}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Store Hours */}
        {vendor.delivery_hours && Object.keys(vendor.delivery_hours).length > 0 && (
          <View style={styles.storeHoursContainer}>
            <StoreHoursDisplay 
              hours={vendor.delivery_hours} 
              compact
              vendorCity={vendor.city}
              vendorState={vendor.state}
              vendorCountry={vendor.country}
            />
          </View>
        )}

        {/* Only show Message Seller button if it's not the user's own vendor profile */}
        {vendor.user_id !== user?.id && (
          <View style={styles.actions}>
            <Button
              onPress={startChat}
              variant="primary"
              style={styles.chatButton}
              icon="message-circle"
              iconColor="#000000"
              loading={isStartingChat}
              disabled={isStartingChat}
            >
              <Text style={styles.chatButtonText}>
                {isStartingChat ? "Starting Chat..." : "Message Seller"}
              </Text>
            </Button>
          </View>
        )}

        <Text style={styles.sectionTitle}>Products</Text>
      </>
    );
  };

  const handleProductPress = (product: Product) => {
    // Use push instead of navigate to ensure a new screen is added to the stack
    // This prevents the issue where back navigation skips the Vendor screen
    navigation.push("ProductDetails", { id: product.id });
  };

  return (
    <>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
      >
        {renderHeader()}
        {products && products.length > 0 ? (
          <ProductGrid
            products={products}
            onProductPress={handleProductPress}
          />
        ) : (
          <EmptyState
            icon="package"
            title="No Products"
            description="This vendor hasn't listed any products yet"
          />
        )}
      </ScrollView>
      <VendorRatingsModal
        visible={showRatingsModal}
        onClose={() => setShowRatingsModal(false)}
        vendorId={vendorId}
        storeName={vendor?.store_name}
      />
    </>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    content: {
      flexGrow: 1,
    },
    banner: {
      width: "100%",
      height: 200,
    },
    bannerPlaceholder: {
      width: "100%",
      height: 150,
      backgroundColor: colors.border,
    },
    storeInfo: {
      flexDirection: "row",
      padding: spacing.lg,
      alignItems: "flex-start",
    },
    logo: {
      width: 80,
      height: 80,
      borderRadius: 40,
    },
    logoPlaceholder: {
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: colors.border,
      justifyContent: "center",
      alignItems: "center",
    },
    logoContainer: {
      alignItems: "center",
      marginRight: spacing.md,
    },
    ratingBadge: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      backgroundColor: colors.backgroundSecondary,
      width: 80,
      paddingVertical: 6,
      borderRadius: 12,
      marginTop: spacing.sm,
    },
    ratingText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
    },
    storeDetails: {
      flex: 1,
    },
    storeName: {
      ...typography.title,
      marginBottom: spacing.xs,
      color: colors.text,
    },
    usernameContainer: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    username: {
      ...typography.body,
      color: colors.primary,
      fontWeight: "500",
    },
    storeDescription: {
      ...typography.body,
      color: colors.text,
      marginBottom: spacing.md,
    },
    storeMetadata: {
      flexDirection: "row",
      gap: spacing.md,
    },
    metadataItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    metadataText: {
      ...typography.caption,
      color: colors.textLight,
    },
    storeHoursContainer: {
      paddingHorizontal: spacing.lg,
      marginBottom: spacing.md,
    },
    actions: {
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    chatButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: spacing.sm,
      backgroundColor: colors.primary,
    },
    sectionTitle: {
      ...typography.subtitle,
      padding: spacing.md,
      color: colors.text,
    },
    loading: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },
  chatButtonText: {
    color: "#000000",
    fontWeight: "600",
    fontSize: 16,
  },
  });
